import { useRequiredContext } from "@redotech/react-util/context";
import { FulfillmentOrder } from "@redotech/redo-model/fulfillment";
import { resolveCarrier } from "@redotech/redo-model/order-tracking/carriers/carrier-resolver";
import { TrackerStatus } from "@redotech/redo-model/order-tracking/order-tracking-tracker-status-definition";
import { OrderTrackingShipmentStatus } from "@redotech/redo-model/order-tracking/order-tracking-types";
import {
  TrackingPageSettings,
  TrackingPageTrackingDetailsBlock,
} from "@redotech/redo-model/order-tracking/tracking-page-definition";
import type {
  TrackingPageOrder,
  TrackingPageShipment,
} from "@redotech/redo-model/order-tracking/tracking-page-order-definition";
import { memo, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Flex } from "../../../flex";
import { pickUpConfig } from "../../../settings-elements/order/pick-up-config-util";
import { Text } from "../../../text";
import { TrackingPageContext } from "../../context/tracking-page-context";
import { getLabelStyles } from "../shared/block-styles";
import { BlockWrapper } from "../shared/block-wrapper";
import {
  getSelectedShipmentWithFallback,
  getShipmentIndex,
} from "../shared/fulfillment-utils";
import { CustomPickupBlock } from "./custom-pickup";
import { DeliveryEstimate } from "./delivery-estimate";
import { getMostRecentUnrecoveredExceptionalStatus } from "./shipment-exceptions";
import { StatusDisplay } from "./status-display";
import { TrackingCarrierInfo } from "./tracking-carrier-info";
import { TrackingDetailLines } from "./tracking-detail-lines";
import * as trackingCss from "./tracking-details.module.css";

export const TrackingDetails = memo(function TrackingDetails({
  optionalSubtext,
}: TrackingPageTrackingDetailsBlock) {
  const { order, environment, fulfillmentOrders, pageSettings, stalledDays } =
    useRequiredContext(TrackingPageContext);

  const { fulfillmentIndex: paramsFulfillmentIndex } = useParams<{
    fulfillmentIndex?: string;
  }>();
  const shipmentIndex = getShipmentIndex(
    paramsFulfillmentIndex,
    environment,
    order,
  );
  const selectedShipment = getSelectedShipmentWithFallback(
    order?.shipments,
    shipmentIndex,
  );

  const robustShipment: TrackingPageShipment | undefined = useMemo(() => {
    if (!selectedShipment) return undefined;
    if (shouldHaveBeenDeliveredButWasnt(selectedShipment)) {
      return { ...selectedShipment, estimatedDeliveryAt: null };
    }
    return selectedShipment;
  }, [selectedShipment]);

  return (
    <BlockWrapper
      environment={environment}
      loading={!order}
      pageSettings={pageSettings}
      withBorder
    >
      {order && (
        <TrackingDetailsForShipment
          fulfillmentOrders={fulfillmentOrders}
          optionalSubtext={optionalSubtext}
          order={order}
          pageSettings={pageSettings}
          shipment={robustShipment}
          stalledDays={stalledDays}
        />
      )}
    </BlockWrapper>
  );
});

const TrackingDetailsForShipment = memo(function TrackingDetailsForShipment({
  shipment,
  order,
  fulfillmentOrders,
  pageSettings,
  optionalSubtext,
  stalledDays,
}: {
  shipment: TrackingPageShipment | undefined;
  order: TrackingPageOrder;
  fulfillmentOrders: FulfillmentOrder[];
  pageSettings: TrackingPageSettings | undefined;
  optionalSubtext: string | undefined;
  stalledDays: number | null;
}) {
  const { returnAppSettings } = useRequiredContext(TrackingPageContext);
  const labelStyles = getLabelStyles(pageSettings);

  const pickupConfig = pickUpConfig(fulfillmentOrders);

  // Only show carrier-provided EDD or no estimate at all. Statistical estimates are unreliable and
  // customers view estimated dates as guarantees. Show "Awaiting ETA" instead.
  const carrierEstimatedDelivery = useMemo(
    () => shipment?.estimatedDeliveryAt ?? null,
    [shipment?.estimatedDeliveryAt],
  );

  const estimatedOrActualDelivery = useMemo(() => {
    const isDelivered =
      shipment?.status === OrderTrackingShipmentStatus.DELIVERED;
    const deliveryDate = shipment?.deliveredAt;
    const resolved = shipment?.carrier
      ? resolveCarrier(shipment.carrier)
      : undefined;
    const eddDistrusted = !!(
      resolved?.found && resolved.definition.distrustDeliveryEstimate
    );
    return isDelivered && deliveryDate
      ? { isDelivered: true, date: deliveryDate, eddDistrusted: false }
      : { isDelivered: false, date: carrierEstimatedDelivery, eddDistrusted };
  }, [shipment, carrierEstimatedDelivery]);

  const trackingEvents = useMemo(() => {
    return shipment?.trackingEvents?.length ? [...shipment.trackingEvents] : [];
  }, [shipment?.trackingEvents]);

  const mostRecentUnrecoveredExceptionalStatus = useMemo(() => {
    return getMostRecentUnrecoveredExceptionalStatus(trackingEvents);
  }, [trackingEvents]);

  const pastPreTransit = useMemo(() => {
    return (
      shipment?.trackingEvents?.some(
        (e) => e.status !== TrackerStatus.PRE_TRANSIT,
      ) ?? false
    );
  }, [shipment?.trackingEvents]);

  const renderTrackingContent = () => {
    const isPosOrder = order.sourceName === "pos";

    if (isPosOrder) {
      return null;
    }

    const shouldShowAndarPickup =
      returnAppSettings.storeUrl === "andar-wallets.myshopify.com" &&
      shipment &&
      shipment.carrier === "Self-Pickup (by PDQ)";

    const shouldShowIvyCityPickup =
      returnAppSettings.storeUrl === "ivy-city-co.myshopify.com" &&
      pickupConfig.isPickUpOrder;

    const shouldShowCustomPickup =
      shouldShowAndarPickup || shouldShowIvyCityPickup;

    if (shouldShowCustomPickup) {
      return (
        <CustomPickupBlock
          isPickUpOrder={pickupConfig.isPickUpOrder}
          pageSettings={pageSettings}
          shipment={shipment}
          storeUrl={returnAppSettings.storeUrl}
        />
      );
    }

    return (
      <>
        <TrackingDetailLines trackingEvents={trackingEvents} />
        <TrackingCarrierInfo shipment={shipment} />
        {optionalSubtext && (
          <Text color="gray" fontSize="sm" style={labelStyles}>
            {optionalSubtext}
          </Text>
        )}
      </>
    );
  };

  return (
    <Flex
      className={trackingCss.trackerCard}
      gap="xl"
      height="full"
      p="3xl"
      w="full"
    >
      <Flex dir="column" flex={1} gap="lg">
        <DeliveryEstimate
          delivery={estimatedOrActualDelivery}
          pastPreTransit={pastPreTransit}
          shipment={shipment}
        />

        <StatusDisplay
          delivery={estimatedOrActualDelivery}
          exceptionalStatus={mostRecentUnrecoveredExceptionalStatus}
          order={order}
          pastPreTransit={pastPreTransit}
          pickupConfig={pickupConfig}
          shipment={shipment}
          stalledDays={stalledDays}
        />

        {renderTrackingContent()}
      </Flex>
    </Flex>
  );
});

function shouldHaveBeenDeliveredButWasnt(shipment: TrackingPageShipment) {
  return !!(
    shipment.status !== OrderTrackingShipmentStatus.DELIVERED &&
    shipment.estimatedDeliveryAt &&
    Temporal.Instant.compare(
      Temporal.Now.zonedDateTimeISO().toInstant(),
      shipment.estimatedDeliveryAt.toTemporalInstant(),
    ) > 0
  );
}
